import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Lock } from 'lucide-react';

interface GuestSignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignup: () => void;
  featureName?: string;
  isDark?: boolean;
}

export function GuestSignupModal({
  isOpen,
  onClose,
  onSignup,
  featureName,
  isDark,
}: GuestSignupModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dark =
    typeof isDark === 'boolean'
      ? isDark
      : typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity" />

      {/* Modal Container */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-modal-title"
        className={`relative w-full max-w-md transform overflow-hidden rounded-2xl border p-6 shadow-2xl transition-all ${
          dark
            ? 'border-zinc-800 bg-[#141a22] text-zinc-100'
            : 'border-zinc-200 bg-white text-zinc-950'
        }`}
      >
        {/* Close Button X */}
        <button
          type="button"
          aria-label="Close dialog"
          onClick={onClose}
          className={`absolute right-4 top-4 rounded-full p-1.5 transition-colors ${
            dark ? 'text-zinc-400 hover:bg-zinc-800 hover:text-white' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900'
          }`}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2.5 rounded-xl ${dark ? 'bg-[#3bf4ce]/10 text-[#3bf4ce]' : 'bg-teal-50 text-teal-700'}`}>
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <h3
              id="guest-modal-title"
              className="text-lg font-bold tracking-tight"
            >
              Create an account to use this feature
            </h3>
            {featureName && (
              <span className={`text-xs font-mono uppercase tracking-wider ${dark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {featureName}
              </span>
            )}
          </div>
        </div>

        {/* Modal Body */}
        <div className="mb-6">
          <p className={`text-sm leading-relaxed ${dark ? 'text-zinc-300' : 'text-zinc-600'}`}>
            Sign up to import your own trades, connect a broker, save your trading history, and personalize your workspace.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2.5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className={`flex h-10 items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
              dark
                ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white'
                : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 hover:text-zinc-900'
            }`}
          >
            Continue Exploring
          </button>
          <button
            type="button"
            onClick={onSignup}
            className="flex h-10 items-center justify-center rounded-xl bg-[#3bf4ce] px-5 py-2 text-sm font-bold text-black transition-colors hover:bg-[#34d399] shadow-lg shadow-[#3bf4ce]/20"
          >
            Create Account
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default GuestSignupModal;
